// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import {
  AssetLogo,
  type AssetLogoType,
} from '@shared/ui-kit/components/asset-logo';
import { windowOpenPopup } from '@shared/ui-kit/components/popups/window-open-popup/window-open-popup';
import { useShowAssetProfile } from '@shared/ui-kit/components/popups/asset-profile/hooks/use-show-asset-profile';
import { classNames } from '@shared/ui-kit/utils';
import { type FC, type ReactNode, useCallback, useMemo } from 'react';

const ASSET_TYPES: Record<AssetLogoType, string[]> = {
  stock: ['common stock', 'stock'],
  etf: ['etf', 'etp'],
  mutual_fund: ['mutual_fund', 'mutual fund'],
  crypto: ['crypto'],
};

const isIsinLike = (value: string): boolean =>
  /^[A-Z]{2}[A-Z0-9]{9}\d$/.test(value);

const resolveProductIdentifier = (
  name: string | null | undefined,
  ticker: string | null | undefined,
  isin: string | null | undefined,
): string | null => {
  const normalizedName = name?.trim().toUpperCase();
  const normalizedTicker = ticker?.trim();
  const normalizedIsin = isin?.trim();

  if (
    normalizedTicker &&
    normalizedTicker !== 'missing' &&
    normalizedTicker.toUpperCase() !== normalizedName &&
    !isIsinLike(normalizedTicker)
  ) {
    return normalizedTicker;
  }

  if (
    normalizedIsin &&
    normalizedIsin !== 'missing' &&
    normalizedIsin.toUpperCase() !== normalizedName
  ) {
    return normalizedIsin;
  }

  if (
    normalizedTicker &&
    normalizedTicker !== 'missing' &&
    normalizedTicker.toUpperCase() !== normalizedName &&
    isIsinLike(normalizedTicker)
  ) {
    return normalizedTicker;
  }

  return null;
};

export interface OperationProductCellProps {
  readonly name?: string | null;
  readonly ticker?: string | null;
  readonly isin?: string | null;
  readonly assetType?: string;
  readonly logoUrl?: string;
  readonly url?: string;
  readonly watchlistButton?: ReactNode;
  readonly cashTitle?: string;
  readonly cashSubtitle?: string;
}

export const OperationProductCell: FC<OperationProductCellProps> = ({
  name,
  ticker,
  isin,
  assetType = '',
  logoUrl,
  url,
  watchlistButton,
  cashTitle,
  cashSubtitle,
}) => {
  const showAssetProfile = useShowAssetProfile();

  const identifiedAssetType = useMemo(
    () =>
      Object.keys(ASSET_TYPES).find(key =>
        ASSET_TYPES[key as AssetLogoType].includes(assetType.toLowerCase()),
      ) as AssetLogoType | undefined,
    [assetType],
  );

  const isStock = identifiedAssetType === 'stock';
  const isETF = identifiedAssetType === 'etf';
  const isMutualFund = identifiedAssetType === 'mutual_fund';
  const canShowProfile = isETF || isMutualFund;
  const canOpenUrl = isStock && !!url && url !== 'missing';

  const handleAction = useCallback(() => {
    if (!ticker) return;

    if (canOpenUrl) {
      windowOpenPopup(url);
    }

    if (canShowProfile && identifiedAssetType) {
      showAssetProfile({
        ticker,
        assetType: identifiedAssetType,
        watchlistButton,
      });
    }
  }, [
    canOpenUrl,
    canShowProfile,
    identifiedAssetType,
    showAssetProfile,
    ticker,
    url,
    watchlistButton,
  ]);

  if (cashTitle) {
    return (
      <div className="flex flex-col">
        <span>{cashTitle}</span>
        {cashSubtitle && <span className="text-sm">{cashSubtitle}</span>}
      </div>
    );
  }

  if (!ticker && !isin) {
    return <span>-</span>;
  }

  const displayName = name?.trim() || '-';
  const identifier = resolveProductIdentifier(name, ticker, isin);
  const isInteractive = canOpenUrl || canShowProfile;

  return (
    <div
      className={classNames(
        'grid min-w-0 grid-cols-[auto_1fr] grid-rows-2 items-center gap-x-2 gap-y-0.5',
        { 'cursor-pointer': isInteractive },
      )}
      onClick={isInteractive ? handleAction : undefined}
      data-testid={ticker ? `operation-product-${ticker}` : 'operation-product'}
    >
      <AssetLogo
        assetType={identifiedAssetType}
        logoUrl={logoUrl}
        className="row-span-2 self-center"
      />
      <Tooltip title={displayName} placement="top">
        <span className="line-clamp-2 min-w-0 leading-tight">{displayName}</span>
      </Tooltip>
      {identifier ? (
        <span className="truncate text-sm text-graph-text">{identifier}</span>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );
};
